import { CATEGORY_LABELS, ROLE_LABELS, SIGNAL_LABELS, STATUS_LABELS } from "./format";

type AuditTarget = {
  label: string;
  fields?: Record<string, unknown>;
};

export type AuditDisplayEntry = {
  action: string;
  entityType?: string;
  entityId?: string;
  metadata?: unknown;
  target?: AuditTarget | null;
  targetSnapshot?: unknown;
};

export type AuditDetail = {
  label: string;
  value?: string;
  before?: string;
  after?: string;
};

export type AuditPresentation = {
  summary: string;
  details: AuditDetail[];
};

const FIELD_LABELS: Record<string, string> = {
  name: "Nom",
  label: "Libellé",
  previousLabel: "Ancien libellé",
  epicPseudo: "Pseudo Epic",
  epicName: "Pseudo Epic",
  epicId: "Epic ID",
  discordPseudo: "Pseudo Discord",
  discordName: "Pseudo Discord",
  discordId: "Discord ID",
  wlsName: "Pseudo WLS",
  owner: "Propriétaire d’équipe",
  category: "Catégorie",
  status: "Statut",
  role: "Rôle",
  reason: "Raison",
  liftReason: "Motif de levée",
  serversBan: "Serveurs concernés",
  tournamentId: "Tournoi",
  eventId: "Événement WLS",
  groupId: "Groupe WLS",
  fileName: "Fichier",
  source: "Source",
  hours: "Durée",
  startsAt: "Début",
  endsAt: "Fin",
  scheduled: "Programmée",
  dmRecipients: "Admins prévenus en DM",
  score: "Score",
  level: "Niveau",
  banEvasion: "Contournement de ban",
  signal: "Signal",
  signals: "Signaux",
  origin: "Origine",
  configured: "Configuration",
  scopes: "Permissions",
  rateLimitPerMinute: "Limite par minute",
  prefix: "Préfixe",
  count: "Nombre d’entrées",
  total: "Lignes traitées",
  inserted: "Entrées ajoutées",
  duplicates: "Entrées déjà connues",
  invalid: "Lignes non exploitables",
  requested: "Demandées",
  updated: "Modifiées",
  lifted: "Sanctions levées",
  previousLifts: "Anciennes levées de blacklist",
  registrants: "Inscrits",
  processedRegistrants: "Inscrits analysés",
  totalRegistrants: "Inscrits reçus",
  registrantsSeen: "Inscrits vus",
  suspects: "Suspects",
  suspectsFound: "Suspects détectés",
  autoAdded: "Contournements ajoutés",
  errorsCount: "Erreurs",
  alerts: "Alertes",
  createBlacklist: "Ajout à la blacklist",
  createdBlacklistId: "Entrée créée",
  checkId: "Contrôle",
  suspectId: "Signalement",
  kind: "Type",
  bulk: "Action groupée",
  error: "Erreur",
  note: "Note",
  addedAt: "Date de sanction",
  value: "Valeur",
};

const SOURCE_LABELS: Record<string, string> = {
  public_leaderboard: "Classement public",
  private_server: "API WLS privée",
  private_browser: "Ancienne extension",
  csv: "Fichier CSV",
  wls_api: "API WLS",
  manual: "Ajout manuel",
  migration: "Migration",
  suspect: "Signalement",
};

const LEVEL_LABELS: Record<string, string> = { high: "Élevé", medium: "Moyen", low: "Faible" };
const ORIGIN_LABELS: Record<string, string> = {
  ban_evasion: "Contournement de ban",
  watch: "Surveillance",
  suspect: "Signalement",
};
const KIND_LABELS: Record<string, string> = {
  evasion: "Contournements de ban",
  blacklisted: "Joueurs déjà blacklistés",
  all: "Tous les signalements",
};
const SCOPE_LABELS: Record<string, string> = {
  "check:read": "Vérifier un joueur",
  "blacklist:read": "Lire la blacklist",
};
const SENSITIVE_FIELD = /(password|secret|token|digest|webhook|credential)/i;

function recordValue(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function fieldLabel(key: string) {
  return FIELD_LABELS[key] ?? key.replace(/([a-z])([A-Z])/g, "$1 $2").replaceAll("_", " ");
}

function redact(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(redact);
  if (value && typeof value === "object") return Object.fromEntries(Object.entries(value)
    .filter(([key]) => !SENSITIVE_FIELD.test(key)).map(([key, child]) => [key, redact(child)]));
  return value;
}

export function auditIdentity(entry: AuditDisplayEntry) {
  const metadata = recordValue(entry.metadata);
  const sources = [recordValue(metadata.after), metadata, recordValue(metadata.registrant), recordValue(metadata.before), recordValue(entry.target?.fields)];
  const find = (keys: string[]) => sources.map(source => firstText(source, keys)).find(Boolean);
  return { epicId: find(["epicId"]), discordId: find(["discordId"]),
    name: find(["epicPseudo", "epicName", "discordPseudo", "discordName", "wlsName", "name", "fileName"]) };
}

function formatValue(key: string, value: unknown): string {
  if (value === undefined || value === null || value === "") return "Non renseigné";
  if (key === "category" && typeof value === "string") return CATEGORY_LABELS[value as keyof typeof CATEGORY_LABELS] ?? value;
  if (key === "status" && typeof value === "string") return STATUS_LABELS[value] ?? value;
  if (key === "role" && typeof value === "string") return ROLE_LABELS[value] ?? value;
  if (key === "signal" && typeof value === "string") return SIGNAL_LABELS[value] ?? value;
  if (key === "source" && typeof value === "string") return SOURCE_LABELS[value] ?? value;
  if (key === "level" && typeof value === "string") return LEVEL_LABELS[value] ?? value;
  if (key === "origin" && typeof value === "string") return ORIGIN_LABELS[value] ?? value;
  if (key === "kind" && typeof value === "string") return KIND_LABELS[value] ?? value;
  if (key === "configured") return value ? "Configuré" : "Non configuré";
  if (key === "hours" && typeof value === "number") return `${value} h`;
  if ((key === "startsAt" || key === "endsAt") && typeof value === "number") {
    return new Intl.DateTimeFormat("fr-FR", { dateStyle: "long", timeStyle: "short" }).format(value);
  }
  if (key === "addedAt" && typeof value === "number") return new Intl.DateTimeFormat("fr-FR", { dateStyle: "long" }).format(value);
  if (typeof value === "boolean") return value ? "Oui" : "Non";
  if (Array.isArray(value)) {
    if (key === "previousLifts") return value.map(item => {
      const lift = recordValue(item);
      const date = typeof lift.liftedAt === "number" ? new Intl.DateTimeFormat("fr-FR", { dateStyle: "long", timeStyle: "short" }).format(lift.liftedAt) : "date inconnue";
      return `Retiré le ${date}${typeof lift.reason === "string" ? ` — ${lift.reason}` : ""} (fiche ${lift.blacklistId})`;
    }).join(" ; ");
    return value.map((item) => {
      if (typeof item !== "string") return String(item);
      if (key === "scopes") return SCOPE_LABELS[item] ?? item;
      return formatValue(key === "signals" ? "signal" : key, item);
    }).join(", ");
  }
  if (typeof value === "object") return JSON.stringify(redact(value));
  return String(value);
}

function firstText(source: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return undefined;
}

export function buildAuditPresentation(entry: AuditDisplayEntry): AuditPresentation {
  const metadata = recordValue(entry.metadata);
  const targetFields = recordValue(entry.target?.fields);
  const details: AuditDetail[] = [];
  const usedLabels = new Set<string>();

  const addValue = (key: string, value: unknown, current = false) => {
    if (SENSITIVE_FIELD.test(key) || value === undefined || value === null || value === "") return;
    const baseLabel = fieldLabel(key);
    if (usedLabels.has(baseLabel)) return;
    usedLabels.add(baseLabel);
    details.push({ label: current ? `${baseLabel} actuel` : baseLabel, value: formatValue(key, value) });
  };

  const before = recordValue(metadata.before);
  const after = recordValue(metadata.after);
  for (const key of new Set([...Object.keys(before), ...Object.keys(after)])) {
    if (SENSITIVE_FIELD.test(key)) continue;
    const beforeValue = formatValue(key, before[key]);
    const afterValue = formatValue(key, after[key]);
    if (beforeValue === afterValue) continue;
    const label = fieldLabel(key);
    usedLabels.add(label);
    details.push({ label, before: beforeValue, after: afterValue });
  }
  if (Object.keys(before).length === 0 && Object.keys(after).length === 0
    && (Object.hasOwn(metadata, "before") || Object.hasOwn(metadata, "after"))) {
    const key = entry.action === "user.set_discord_id" ? "discordId"
      : entry.action === "user.set_role" ? "role" : "value";
    const beforeValue = formatValue(key, metadata.before);
    const afterValue = formatValue(key, metadata.after);
    if (beforeValue !== afterValue) {
      const label = fieldLabel(key);
      usedLabels.add(label);
      details.push({ label, before: beforeValue, after: afterValue });
    }
  }

  if (entry.action === "api.check") {
    const blacklisted = metadata.blacklisted === true;
    const matched = metadata.matched === true;
    details.push({ label: "Résultat", value: blacklisted ? "Joueur blacklisté" : matched ? "Correspondance à vérifier" : "Aucune correspondance" });
    usedLabels.add("Résultat");
  }

  for (const [key, value] of Object.entries(metadata)) {
    if (["before", "after", "blacklisted", "matched"].includes(key)) continue;
    addValue(key, value);
  }

  for (const [key, value] of Object.entries(targetFields)) addValue(key, value, !entry.targetSnapshot);

  if (entry.entityId) details.push({ label: "Identifiant technique", value: entry.entityId });

  const identity = auditIdentity(entry).name || entry.target?.label;
  const category = metadata.category ?? targetFields.category;
  const tournament = metadata.tournamentId ?? targetFields.tournamentId;
  const exportedCount = entry.action === "api.blacklist_list" && typeof metadata.count === "number" ? metadata.count : undefined;
  let summary = exportedCount !== undefined
    ? `${exportedCount} entrée${exportedCount > 1 ? "s" : ""} exportée${exportedCount > 1 ? "s" : ""}`
    : identity || (typeof tournament === "string" ? `Tournoi ${tournament}` : undefined) || "Détails de l’action";

  if (entry.action === "api.check") {
    const result = metadata.blacklisted === true
      ? "joueur blacklisté"
      : metadata.matched === true ? "correspondance à vérifier" : "aucune correspondance";
    summary = `${summary} · ${result}`;
  } else if (category !== undefined && category !== null) {
    summary = `${summary} · ${formatValue("category", category)}`;
  } else if (identity && typeof tournament === "string") {
    summary = `${summary} · tournoi ${tournament}`;
  }

  return { summary, details };
}
